import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../config/siteConfig';

const LOGO_WHITE = siteConfig.logo;

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();

  const navLinks = siteConfig.navigation.map(link => ({
    name: (t.nav as any)[link.key],
    path: link.path
  }));

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-maat-blue border-b border-maat-gold/10 z-50 transition-all shadow-sm">
      <div className="container mx-auto px-6 h-full flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-3">
          <img src={LOGO_WHITE} alt="Met Maät Logo" className="h-9 w-auto" />
          <span translate="no" className="text-maat-stone font-serif text-xl md:text-2xl font-bold tracking-tight leading-none">
            {siteConfig.name} <span className="text-maat-gold font-light">{siteConfig.nameAccent}</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center space-x-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-xs uppercase tracking-widest transition-colors duration-300 font-bold ${location.pathname === link.path ? 'text-maat-gold' : 'text-maat-stone/80 hover:text-maat-gold'
                }`}
            >
              {link.name}
            </Link>
          ))}

          <div className="flex items-center space-x-2 border-l border-maat-gold/20 pl-6 ml-2">
            <button
              onClick={() => setLanguage('en')}
              className={`text-xs font-bold transition-colors ${language === 'en' ? 'text-maat-gold' : 'text-maat-stone/40 hover:text-maat-stone'}`}
            >
              EN
            </button>
            <span className="text-maat-stone/10 text-xs">|</span>
            <button
              onClick={() => setLanguage('nl')}
              className={`text-xs font-bold transition-colors ${language === 'nl' ? 'text-maat-gold' : 'text-maat-stone/40 hover:text-maat-stone'}`}
            >
              NL
            </button>
          </div>

          <Link
            to="/contact"
            className="bg-maat-gold text-maat-blue px-6 py-2 rounded-lg text-sm font-bold hover:bg-maat-stone transition-all transform hover:scale-105 active:scale-95 shadow-md"
          >
            {t.nav.contact}
          </Link>
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden text-maat-stone p-2 focus:outline-none"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav Overlay */}
      <div className={`fixed inset-0 bg-maat-blue z-40 transition-transform duration-500 transform ${isOpen ? 'translate-x-0' : 'translate-x-full'} md:hidden`}>
        <div className="flex flex-col items-center justify-center h-full space-y-10">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-2xl font-serif tracking-widest ${location.pathname === link.path ? 'text-maat-gold' : 'text-maat-stone'
                }`}
            >
              {link.name}
            </Link>
          ))}

          <div className="flex items-center space-x-6 py-4">
            <button onClick={() => { setLanguage('en'); setIsOpen(false); }} className={`text-xl font-bold ${language === 'en' ? 'text-maat-gold' : 'text-maat-stone/30'}`}>EN</button>
            <button onClick={() => { setLanguage('nl'); setIsOpen(false); }} className={`text-xl font-bold ${language === 'nl' ? 'text-maat-gold' : 'text-maat-stone/30'}`}>NL</button>
          </div>

          <Link
            to="/contact"
            onClick={() => setIsOpen(false)}
            className="bg-maat-gold text-maat-blue px-10 py-4 rounded-3xl text-lg font-semibold"
          >
            {t.nav.contact}
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;